/**
 * Curated specialisation tag vocabulary. Phase 1.0 OneNebula starter set,
 * derived from the Employee Handbook + likely future internal docs.
 * Mid-grain so the chip rail isn't sparse but isn't noise either.
 * Cap per doc is 10 (see InlineSpecialisationsCell maxItems).
 *
 * Iterate freely — adding/removing values here is a one-line change. The
 * filter rail re-renders, existing tagged chunks keep their tags
 * (orphaned values just stop being filterable until re-applied).
 *
 * Keep alphabetical so the chip rail renders predictably.
 */
export const CANONICAL_SPECIALISATIONS = [
    'Code of Conduct',
    'Compensation',
    'Confidentiality',
    'Discipline & Grievance',
    'Diversity & Inclusion',
    'Health & Safety',
    'Hiring',
    'IT & Tools',
    'Leave',
    'Performance Management',
    'Remote Work',
    'Time & Attendance',
    'Training & Development',
    'Travel & Expenses',
    'Ways of Working',
];
/** Set form for O(1) membership checks in the seed normalizer. */
export const CANONICAL_SPECIALISATIONS_SET = new Set(CANONICAL_SPECIALISATIONS);
/**
 * Ring 1 (product) specialisations — covers OneView, Service Desk, Galaxy
 * and related telecom-service tooling. Distinct vocabulary from the Ring 0
 * HR list above; each ring's tagger is constrained to its own enum at
 * decode-time. Keep alphabetical to match the chip rail's render order.
 */
export const CANONICAL_PRODUCT_SPECIALISATIONS = [
    'Admin',
    'Auditing',
    'Bulk Operations',
    'Cloud',
    'Data Model',
    'Deployments',
    'End User',
    'Galaxy',
    'Integrations',
    'Mobile',
    'Patching',
    'Service Desk',
    'Setup',
    'Voice',
    'WAN/LAN',
];
export const CANONICAL_PRODUCT_SPECIALISATIONS_SET = new Set(CANONICAL_PRODUCT_SPECIALISATIONS);
/**
 * Combined deduped, alphabetically-sorted list of every canonical
 * specialisation across rings. The /documents chip rail iterates this so
 * users browsing a mixed corpus see HR (ring 0) and product (ring 1)
 * chips together. Filter logic still operates on the raw tag strings
 * stored on each document.
 */
export const ALL_SPECIALISATIONS = Array.from(new Set([...CANONICAL_SPECIALISATIONS, ...CANONICAL_PRODUCT_SPECIALISATIONS])).sort((a, b) => a.localeCompare(b));
/** Set form for O(1) membership checks across both rings. The PATCH
 * validator on /api/v1/admin/knowledgebase/documents accepts any tag in
 * either ring's vocabulary so editing a Ring 1 doc's tags doesn't trip
 * "Unknown specialisation" on its preserved product tags. */
export const ALL_SPECIALISATIONS_SET = new Set(ALL_SPECIALISATIONS);
/**
 * Canonical document-type vocabulary for the OneNebula corpus. Used by
 * the Python ingest tagger (auto-classifies on title + first chunk),
 * the /documents Type filter, and the /admin/documents inline editor.
 * Distinct from specialisations: this is the *genre* of the document
 * (handbook vs policy vs runbook), not the topic.
 *
 * Values are stored on `DocumentRegistryEntry.documentType` and on
 * every chunk (denormalized). Adding a value requires a code change so
 * the inline editor's dropdown stays in lockstep with what the auto-
 * tagger is allowed to emit. Keep alphabetical. Iterate as the corpus
 * grows; external-customer doc types (Phase 1.5+) will likely add
 * variants like "Customer Onboarding Spec" or "Integration Doc".
 */
export const CANONICAL_DOCUMENT_TYPES = [
    'Case Study',
    'FAQ',
    'Form',
    'Guide',
    'Handbook',
    'Policy',
    'Procedure',
    'Reference',
    'Runbook',
    'Source Code',
    'Template',
    'Way of Working',
];
export const CANONICAL_DOCUMENT_TYPES_SET = new Set(CANONICAL_DOCUMENT_TYPES);
//# sourceMappingURL=sociale-kaart.js.map